import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

export interface CartLine {
  product_id: string;
  slug: string;
  name: string;
  sku: string;
  image: string | null;
  price_cents: number;
  /** Whether the product's primary image has a transparent background. */
  is_transparent?: boolean;
}

interface CartCtx {
  lines: CartLine[];
  count: number;
  subtotal_cents: number;
  add: (line: CartLine) => void;
  remove: (productId: string) => void;
  has: (productId: string) => boolean;
  clear: () => void;
  open: boolean;
  setOpen: (b: boolean) => void;
}

const Ctx = createContext<CartCtx | null>(null);

const STORAGE_KEY = 'tcp_cart_v1';

function readStoredLines(): CartLine[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  // Hydrate synchronously from localStorage during the first render. A separate
  // load effect would race the persist effect below and wipe a returning cart
  // (notably under React StrictMode's double-invoked effects).
  const [lines, setLines] = useState<CartLine[]>(readStoredLines);
  const [open, setOpen] = useState(false);
  const linesRef = useRef(lines);
  linesRef.current = lines;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch {/* ignore */}
  }, [lines]);

  const add = useCallback((line: CartLine) => {
    setLines((prev) => (prev.some((l) => l.product_id === line.product_id) ? prev : [...prev, line]));
    setOpen(true);
  }, []);
  const remove = useCallback((productId: string) => {
    setLines((prev) => prev.filter((l) => l.product_id !== productId));
  }, []);
  const has = useCallback(
    (productId: string) => linesRef.current.some((l) => l.product_id === productId),
    [],
  );
  const clear = useCallback(() => setLines([]), []);
  const subtotal_cents = useMemo(
    () => lines.reduce((s, l) => s + l.price_cents, 0),
    [lines],
  );

  const value = useMemo<CartCtx>(
    () => ({ lines, count: lines.length, subtotal_cents, add, remove, has, clear, open, setOpen }),
    [lines, subtotal_cents, add, remove, has, clear, open],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCart() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
